import React, { useState, useContext, useEffect } from 'react'
import { Link } from 'react-router-dom'
import ResponsiveAppBar from '../../components/Navbar'
import { Container, Button, Alert } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import { UserContext } from '../../context/UserProvider'
import { getList } from '../../services/order'
import { orderDays, summarizeOrders } from './metrics'
import './dashboard.css'
import ARDashboardSummary from '../AccountsReceivable/DashboardSummary'

const dateLabel = date => new Date(date + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
const number = value => value.toLocaleString('en-US')

export default function Dashboard() {
  const { token, access } = useContext(UserContext)
  const allowed = Boolean(access?.control?.orders)
  const [days, setDays] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [revision, setRevision] = useState(0)
  useEffect(() => {
    if (!token || !allowed) return
    let active = true
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const range = orderDays()
        const orders = []
        let page = 1
        let total = Infinity
        while (orders.length < total) {
          const response = await getList(token, {
            'user-specific': false, 'current-page': page, 'record-per-page': 250,
            'date-filter': { start: range[0].date, end: range[29].date },
          })
          if (!active) return
          if (!Array.isArray(response?.collection) || !Number.isFinite(Number(response['data-total']))) throw new Error('Invalid order response')
          total = Number(response['data-total'])
          if (!response.collection.length && orders.length < total) throw new Error('Incomplete order response')
          orders.push(...response.collection)
          page += 1
        }
        if (active) setDays(summarizeOrders(orders, range))
      } catch {
        if (active) setError('Unable to load order totals. Please try again.')
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [token, allowed, revision])

  const total = days?.reduce((sum, day) => sum + day.count, 0) ?? 0
  const max = Math.max(1, ...(days || []).map(day => day.count))
  const cards = days ? [days[29].count, days[28].count, days.slice(-7).reduce((sum, day) => sum + day.count, 0)] : []
  return <>
    <ResponsiveAppBar />
    <Container maxWidth="xl">
      <main className="order-dashboard">
        <header className="dashboard-heading">
          <div><p className="dashboard-eyebrow">STORE2DOOR DIRECT</p><h1>Dashboard Summary</h1><p>Your latest order activity, at a glance.</p></div>
          {allowed && <div className="dashboard-actions"><Button startIcon={<RefreshIcon />} disabled={loading} onClick={() => setRevision(value => value + 1)}>Refresh</Button><Button variant="contained" component={Link} to="/orders">View orders</Button></div>}
        </header>
        <ARDashboardSummary />
        {!allowed ? <Alert severity="info">Order reporting is not available for your account.</Alert> : <>
          {error && <Alert severity="error" action={<Button onClick={() => setRevision(value => value + 1)}>Retry</Button>}>{error}</Alert>}
          <section className="dashboard-metrics" aria-label="Order totals" aria-busy={loading}>
            {['Orders today', 'Orders yesterday', 'Orders in the last 7 days'].map((title, index) => <article className="dashboard-card" key={title}>
              <div className="metric-label"><h2>{title}</h2><ShoppingBagOutlinedIcon aria-hidden="true" /></div>
              <strong>{loading ? '…' : error ? '—' : number(cards[index] ?? 0)}</strong>
              <p>{index === 2 ? 'Including today' : days ? dateLabel(days[29 - index].date) : 'Loading date'}</p>
            </article>)}
          </section>
          <section className="dashboard-chart" aria-label="Orders over the last 30 days" aria-busy={loading}>
            <div className="chart-heading"><div><h2>Orders over the last 30 days</h2><p>Daily order count · Eastern time · All statuses</p></div>{!loading && !error && <div className="chart-total"><strong>{number(total)}</strong><span>total orders</span></div>}</div>
            {loading ? <div className="chart-message" role="status">Loading order activity…</div> : error ? <div className="chart-message">Order activity is unavailable.</div> : <>
              {total === 0 && <p className="chart-empty">No orders in this period.</p>}
              <div className="bar-chart">
                <div className="chart-scale" aria-hidden="true"><span>{max}</span><span>{Math.round(max / 2)}</span><span>0</span></div>
                <div className="chart-bars">{days.map((day, index) => <div className="chart-column" key={day.date}>
                  <div className="bar-track"><button type="button" className={index === 29 ? 'order-bar today' : 'order-bar'} style={{ height: `${Math.max(day.count ? 1 : 0, day.count / max * 100)}%` }} aria-label={`${dateLabel(day.date)}: ${day.count} orders`}><span className="bar-tooltip">{dateLabel(day.date)} · {number(day.count)} orders</span></button></div>
                  <span className="chart-date" aria-hidden="true">{index % 7 === 0 || index === 29 ? dateLabel(day.date) : ''}</span>
                </div>)}</div>
              </div>
              <details className="chart-data"><summary>View daily totals</summary><table><caption>Daily order counts (Eastern time)</caption><thead><tr><th>Date</th><th>Orders</th></tr></thead><tbody>{days.map(day => <tr key={day.date}><td>{dateLabel(day.date)}</td><td>{number(day.count)}</td></tr>)}</tbody></table></details>
            </>}
          </section>
          <p className="dashboard-note">Counts use the order creation date in Eastern time. The last 7 and 30 days include today.</p>
        </>}
      </main>
    </Container>
  </>
}
